import React, { useCallback, useEffect, useState } from 'react';
import { Col, Typography, Space, Button, Table, Spin, Alert, DatePicker, Tag, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined,
  PlusOutlined,
  FileTextOutlined,
  ToolOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ExceptionOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { ControlTowerSubQueuePanel } from '@/components/control-tower/ControlTowerSubQueuePanel';
import { QmsAttentionBrief } from '@/components/quality/control-tower/QmsAttentionBrief';
import { QmsOutlookRibbon } from '@/components/quality/control-tower/QmsOutlookRibbon';
import { QmsNcrCapaFollowUpZone } from '@/components/quality/control-tower/QmsNcrCapaFollowUpZone';
import {
  getQualityDashboardActionSummary,
  emptyQualityDashboardSummary,
  type QualityDashboardActionSummary,
  type PendingIIRRow,
  type PendingStageInspectionRow,
  type PendingFIRRow,
  type PendingIRNApprovalRow,
  type PendingQmsFirReportRow,
  type PendingPlanItemRow,
  type PlanUnderReviewRow,
} from '@/services/qualityDashboard.service';

const { Title, Text, Paragraph } = Typography;

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const QualityDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [summary, setSummary] = useState<QualityDashboardActionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [asOnDate, setAsOnDate] = useState<string | undefined>(undefined);

  const fetchSummary = useCallback(() => {
    setLoading(true);
    setError(null);
    getQualityDashboardActionSummary(asOnDate ? { as_on_date: asOnDate } : undefined)
      .then(setSummary)
      .catch((e: any) => setError(e?.message ?? 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [asOnDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading && !summary) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const s = summary ?? emptyQualityDashboardSummary();
  const woGateTotal = s.wo_gates_waiting_count;
  const approvalTotal = s.approvals_waiting_count;

  const actionCol = <T extends { link_path: string }>(label = 'View') => ({
    title: 'Action',
    key: 'action',
    render: (_: unknown, row: T) => (
      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(row.link_path)}>
        {label}
      </Button>
    ),
  });

  const iirColumns: ColumnsType<PendingIIRRow> = [
    { title: 'IIR #', dataIndex: 'iir_number', key: 'iir_number' },
    { title: 'WO', dataIndex: 'work_order_number', key: 'work_order_number', render: (v) => v || '—' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (v) => <Tag>{v}</Tag> },
    actionCol<PendingIIRRow>('Open'),
  ];

  const stageColumns: ColumnsType<PendingStageInspectionRow> = [
    { title: 'Inspection #', dataIndex: 'inspection_number', key: 'inspection_number' },
    { title: 'WO', dataIndex: 'work_order_number', key: 'work_order_number', render: (v) => v || '—' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (v) => <Tag>{v}</Tag> },
    actionCol<PendingStageInspectionRow>('Open'),
  ];

  const firColumns: ColumnsType<PendingFIRRow> = [
    { title: 'FIR #', dataIndex: 'fir_number', key: 'fir_number' },
    { title: 'WO', dataIndex: 'work_order_number', key: 'work_order_number', render: (v) => v || '—' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (v) => <Tag>{v}</Tag> },
    actionCol<PendingFIRRow>('Open'),
  ];

  const planItemColumns: ColumnsType<PendingPlanItemRow> = [
    { title: 'Plan #', dataIndex: 'plan_number', key: 'plan_number', render: (v) => v || '—' },
    { title: 'Inspection item', dataIndex: 'description', key: 'description' },
    { title: 'Type', dataIndex: 'inspection_type', key: 'inspection_type', render: (v) => <Tag>{v}</Tag> },
    { title: 'Scheduled', dataIndex: 'scheduled_date', key: 'scheduled_date', render: (v) => v || '—' },
    actionCol<PendingPlanItemRow>('View plan'),
  ];

  const irnColumns: ColumnsType<PendingIRNApprovalRow> = [
    { title: 'IRN #', dataIndex: 'irn_number', key: 'irn_number' },
    { title: 'WO', dataIndex: 'work_order_number', key: 'work_order_number', render: (v) => v || '—' },
    actionCol<PendingIRNApprovalRow>('Approve'),
  ];

  const reportColumns: ColumnsType<PendingQmsFirReportRow> = [
    { title: 'Report #', dataIndex: 'fir_number', key: 'fir_number' },
    { title: 'WO', dataIndex: 'work_order_number', key: 'work_order_number', render: (v) => v || '—' },
    actionCol<PendingQmsFirReportRow>('Review'),
  ];

  const planReviewColumns: ColumnsType<PlanUnderReviewRow> = [
    { title: 'Plan #', dataIndex: 'plan_number', key: 'plan_number' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (v) => <Tag>{v}</Tag> },
    actionCol<PlanUnderReviewRow>('Review'),
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: token.colorBgContainer,
            paddingBottom: 8,
          }}
        >
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              Quality Management Control Tower
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 4 }}>
              Workflow-connected view across WO inspection gates, approvals, NCR/CAPA queues, and
              quality plan actions — true counts, not preview caps.
            </Paragraph>
            <Space wrap size={[4, 4]}>
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => scrollToSection('attention-brief')}
              >
                Attention
              </Button>
              <Text type="secondary">·</Text>
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => scrollToSection('outlook')}
              >
                Outlook
              </Button>
              <Text type="secondary">·</Text>
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => scrollToSection('ncr-capa-follow-up')}
              >
                NCR / CAPA
              </Button>
              <Text type="secondary">·</Text>
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => scrollToSection('wo-inspection-gates')}
              >
                WO gates
              </Button>
              <Text type="secondary">·</Text>
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => scrollToSection('pending-approvals')}
              >
                Approvals
              </Button>
              <Text type="secondary">·</Text>
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => scrollToSection('plan-items')}
              >
                Plan items
              </Button>
            </Space>
          </div>
          <Space wrap>
            <Space>
              <Text type="secondary">As on date:</Text>
              <DatePicker
                value={asOnDate ? dayjs(asOnDate) : dayjs()}
                onChange={(d) => setAsOnDate(d ? d.format('YYYY-MM-DD') : undefined)}
                allowClear
              />
            </Space>
            <Button icon={<ReloadOutlined />} onClick={fetchSummary} loading={loading}>
              Refresh
            </Button>
          </Space>
        </div>

        {error && <Alert type="error" message={error} />}

        <QmsAttentionBrief summary={s} />
        <QmsOutlookRibbon summary={s} />

        <ControlTowerSectionCard zone="actions" title="Quick Actions">
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/qms/quality-plans')}>
              Quality plans
            </Button>
            <Button icon={<FileTextOutlined />} onClick={() => navigate('/qms/initial-inspection-requests')}>
              IIR queue
            </Button>
            <Button icon={<CheckCircleOutlined />} onClick={() => navigate('/qms/inspections')}>
              Inspections
            </Button>
            <Button icon={<ClockCircleOutlined />} onClick={() => navigate('/qms/dimensional-inspection')}>
              Stage inspection
            </Button>
            <Button icon={<WarningOutlined />} onClick={() => navigate('/qms/ncrs')}>
              NCRs
            </Button>
            <Button icon={<ExceptionOutlined />} onClick={() => navigate('/qms/capas')}>
              CAPAs
            </Button>
            <Button icon={<FileTextOutlined />} onClick={() => navigate('/qms/final-inspection-reports')}>
              FIR reports
            </Button>
            <Button icon={<FileTextOutlined />} onClick={() => navigate('/qms/inspection-release-notes')}>
              IRN approvals
            </Button>
            <Button icon={<FileTextOutlined />} onClick={() => navigate('/qms/certificates-of-conformity')}>
              Certificate of Conformity
            </Button>
            <Button icon={<ToolOutlined />} onClick={() => navigate('/qms/tmmde')}>
              Equipment&apos;s
            </Button>
          </Space>
        </ControlTowerSectionCard>

        <QmsNcrCapaFollowUpZone summary={s} asOnDate={asOnDate} />

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <ControlTowerMetricStrip zone="metricsOrders" title="WO inspection pipeline">
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="IIR pending"
                count={s.pending_iir_count}
                icon={<FileTextOutlined />}
                urgency={s.pending_iir_count > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'View queue',
                  onClick: () => scrollToSection('wo-inspection-gates'),
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Stage inspections pending"
                count={s.pending_stage_count}
                icon={<ClockCircleOutlined />}
                urgency={s.pending_stage_count > 0 ? 'attention' : 'neutral'}
                extra={
                  s.failed_stage_count > 0 ? (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {s.failed_stage_count} failed
                    </Text>
                  ) : undefined
                }
                footerLink={{
                  label: 'View queue',
                  onClick: () => scrollToSection('wo-inspection-gates'),
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="FIR pending"
                count={s.pending_fir_count}
                icon={<FileTextOutlined />}
                urgency={s.pending_fir_count > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'View queue',
                  onClick: () => scrollToSection('wo-inspection-gates'),
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Gates waiting (total)"
                count={woGateTotal}
                icon={<ToolOutlined />}
                urgency={woGateTotal > 0 ? 'attention' : 'neutral'}
              />
            </Col>
          </ControlTowerMetricStrip>

          <ControlTowerMetricStrip zone="metricsDocPrep" title="Approvals and release">
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="IRN approvals"
                count={s.pending_irn_count}
                icon={<ExceptionOutlined />}
                urgency={s.pending_irn_count > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'View queue',
                  onClick: () => scrollToSection('pending-approvals'),
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="QMS FIR pending"
                count={s.pending_qms_fir_count}
                icon={<FileTextOutlined />}
                urgency={s.pending_qms_fir_count > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'View queue',
                  onClick: () => scrollToSection('pending-approvals'),
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Plans under review"
                count={s.plans_under_review_count}
                icon={<ExceptionOutlined />}
                urgency={s.plans_under_review_count > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'View queue',
                  onClick: () => scrollToSection('pending-approvals'),
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Pass rate (30d)"
                count={0}
                valueDisplay={
                  s.inspection_pass_rate_30d == null ? '—' : `${s.inspection_pass_rate_30d}%`
                }
                icon={<CheckCircleOutlined />}
                urgency={
                  s.inspection_pass_rate_30d != null && s.inspection_pass_rate_30d < 90
                    ? 'attention'
                    : 'neutral'
                }
              />
            </Col>
          </ControlTowerMetricStrip>

          <ControlTowerMetricStrip zone="metricsDrafts" title="Plans and audits">
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Pending plan items"
                count={s.pending_plan_item_count}
                icon={<FileTextOutlined />}
                urgency={s.pending_plan_item_count > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'View queue',
                  onClick: () => scrollToSection('plan-items'),
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Due in 7 days"
                count={s.plan_items_due_soon_count}
                icon={<ClockCircleOutlined />}
                urgency={s.plan_items_due_soon_count > 0 ? 'attention' : 'neutral'}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Draft plans"
                count={s.draft_plan_count}
                icon={<FileTextOutlined />}
                urgency={s.draft_plan_count > 0 ? 'attention' : 'neutral'}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Scheduled audits"
                count={s.scheduled_audit_count}
                icon={<ClockCircleOutlined />}
                urgency={s.scheduled_audit_count > 0 ? 'attention' : 'neutral'}
              />
            </Col>
          </ControlTowerMetricStrip>
        </Space>

        <ControlTowerSectionCard
          zone="pendingApprovals"
          sectionId="wo-inspection-gates"
          title={`WO inspection gates (${woGateTotal})`}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <ControlTowerSubQueuePanel
              zone="qmsGateIir"
              title="Initial inspection requests"
              count={s.pending_iir_count}
            >
              {s.pending_iirs.length === 0 ? (
                <Text type="secondary">None</Text>
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  columns={iirColumns}
                  dataSource={s.pending_iirs}
                />
              )}
              {s.pending_iir_count > s.pending_iirs.length ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Showing {s.pending_iirs.length} of {s.pending_iir_count} — open IIR queue for full
                  list.
                </Text>
              ) : null}
            </ControlTowerSubQueuePanel>
            <ControlTowerSubQueuePanel
              zone="qmsGateStage"
              title="Stage inspections pending"
              count={s.pending_stage_count + s.failed_stage_count}
            >
              {s.pending_stage_inspections.length + s.failed_stage_inspections.length === 0 ? (
                <Text type="secondary">None</Text>
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  columns={stageColumns}
                  dataSource={[...s.failed_stage_inspections, ...s.pending_stage_inspections]}
                />
              )}
            </ControlTowerSubQueuePanel>
            <ControlTowerSubQueuePanel
              zone="qmsGateFir"
              title="Final inspection requests"
              count={s.pending_fir_count}
            >
              {s.pending_firs.length === 0 ? (
                <Text type="secondary">None</Text>
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  columns={firColumns}
                  dataSource={s.pending_firs}
                />
              )}
            </ControlTowerSubQueuePanel>
          </Space>
        </ControlTowerSectionCard>

        <ControlTowerSectionCard
          zone="pendingApprovals"
          sectionId="pending-approvals"
          title={`Pending approvals (${approvalTotal})`}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <ControlTowerSubQueuePanel
              zone="qmsApprovalIrn"
              title="IRN approvals"
              count={s.pending_irn_count}
            >
              {s.pending_irn_approvals.length === 0 ? (
                <Text type="secondary">None</Text>
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  columns={irnColumns}
                  dataSource={s.pending_irn_approvals}
                />
              )}
            </ControlTowerSubQueuePanel>
            <ControlTowerSubQueuePanel
              zone="qmsApprovalFirReport"
              title="QMS FIR report approvals"
              count={s.pending_qms_fir_count}
            >
              {s.pending_qms_fir_reports.length === 0 ? (
                <Text type="secondary">None</Text>
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  columns={reportColumns}
                  dataSource={s.pending_qms_fir_reports}
                />
              )}
            </ControlTowerSubQueuePanel>
            <ControlTowerSubQueuePanel
              zone="qmsApprovalPlan"
              title="Plans under review"
              count={s.plans_under_review_count}
            >
              {s.plans_under_review.length === 0 ? (
                <Text type="secondary">None</Text>
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  columns={planReviewColumns}
                  dataSource={s.plans_under_review}
                />
              )}
            </ControlTowerSubQueuePanel>
          </Space>
        </ControlTowerSectionCard>

        <ControlTowerSectionCard
          zone="qmsPlanItemsQueue"
          title={`Pending quality plan items (${s.pending_plan_item_count})`}
          sectionId="plan-items"
        >
          {s.pending_plan_items.length === 0 ? (
            <Text type="secondary">No pending quality plan items.</Text>
          ) : (
            <Table
              size="small"
              pagination={false}
              rowKey="id"
              columns={planItemColumns}
              dataSource={s.pending_plan_items}
            />
          )}
          {s.pending_plan_item_count > s.pending_plan_items.length ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Showing {s.pending_plan_items.length} of {s.pending_plan_item_count} — open quality
              plans for the full list.
            </Text>
          ) : null}
        </ControlTowerSectionCard>
      </Space>
    </div>
  );
};

export default QualityDashboardPage;
